import { useSiteSettings } from "@/contexts/SettingsContext";
import { ScrollText, ShieldCheck, RotateCcw } from "lucide-react";
import DOMPurify from "dompurify";

const KEYS = {
  cgu: {
    field: "cgu_content",
    icon: ScrollText,
    title: "Conditions générales d'utilisation",
    subtitle: "Lecture obligatoire avant d'utiliser la plateforme.",
    testId: "legal-cgu",
    fallback: (appName, contactEmail) => `# Conditions générales d'utilisation

Dernière mise à jour : ${new Date().toLocaleDateString("fr-CA")}

## 1. Acceptation
En créant un compte ou en utilisant ${appName}, vous acceptez les présentes conditions générales d'utilisation (CGU). Si vous n'acceptez pas ces conditions, n'utilisez pas la plateforme.

## 2. Description du service
${appName} est une plateforme de mise en relation entre propriétaires de terrains privés (« Hôtes ») et campeurs (« Campeurs ») pour la location de courte durée d'emplacements de camping (tente, van, VR, etc.). ${appName} n'est pas l'hôte du séjour : nous facilitons uniquement la transaction.

## 3. Compte utilisateur
Vous devez fournir des informations exactes lors de l'inscription. Vous êtes responsable de la confidentialité de votre mot de passe et de toute activité réalisée depuis votre compte. Toute fraude ou usurpation d'identité entraînera la suspension immédiate du compte.

## 4. Obligations du Campeur
Le Campeur s'engage à :
- Respecter les règles fixées par l'Hôte et les lois municipales, provinciales et fédérales applicables (camping, feux, bruit, faune, alcool, déchets, etc.).
- Ne causer aucun dommage au terrain, à ses installations ou à l'environnement.
- Quitter les lieux à la date de départ convenue.

## 5. Obligations de l'Hôte
L'Hôte s'engage à :
- Décrire son terrain de façon honnête et à jour.
- Maintenir un compte Stripe Connect actif pour recevoir les paiements.
- Répondre aux demandes de réservation dans les 48 heures.
- Garantir l'accès au terrain pendant la période réservée.

## 6. Paiements
Les paiements sont traités par Stripe Inc. ${appName} prélève des frais de service sur chaque transaction (taux affiché lors de la réservation). Les paiements aux hôtes sont versés 24 heures après la fin du séjour, sous réserve de l'absence de litige.

## 7. Annulation
Voir notre Politique d'annulation pour les modalités complètes.

## 8. Limitation de responsabilité
${appName} agit comme intermédiaire et **ne peut être tenu responsable** des dommages, blessures, infractions, amendes, litiges, vols ou pertes survenant lors d'un séjour. Les Campeurs et Hôtes assument l'entière responsabilité de leurs actes.

## 9. Modification des CGU
${appName} se réserve le droit de modifier ces CGU à tout moment. Les utilisateurs seront informés des changements substantiels par courriel.

## 10. Droit applicable
Les présentes CGU sont régies par le droit en vigueur dans la province de Québec, Canada. Tout litige sera soumis aux tribunaux compétents du district judiciaire de Montréal.

## 11. Contact
Pour toute question relative aux CGU : ${contactEmail || "contact@example.com"}`,
  },
  privacy: {
    field: "privacy_content",
    icon: ShieldCheck,
    title: "Politique de confidentialité",
    subtitle: "Comment vos données personnelles sont collectées, utilisées et protégées.",
    testId: "legal-privacy",
    fallback: (appName, contactEmail) => `# Politique de confidentialité

Dernière mise à jour : ${new Date().toLocaleDateString("fr-CA")}

${appName} respecte votre vie privée et se conforme à la **Loi 25** (modernisation des dispositions législatives en matière de protection des renseignements personnels au Québec), à la **LPRPDE** (loi fédérale canadienne) et au **RGPD** lorsqu'applicable.

## 1. Renseignements que nous collectons
Nous recueillons les renseignements suivants :
- **Identité** : nom, prénom, courriel, numéro de téléphone, photo de profil.
- **Vérification d'identité** : photo de votre pièce d'identité (uniquement pour validation, supprimée après vérification).
- **Paiement** : informations bancaires traitées et stockées exclusivement par Stripe Inc. ${appName} n'accède jamais aux numéros de carte.
- **Localisation** : adresse postale des terrains publiés (Hôtes).
- **Usage** : journaux de connexion (IP, navigateur), réservations, messages, avis.

## 2. Finalités d'utilisation
Vos renseignements servent à :
- Fournir et améliorer le service (création de compte, réservation, paiement, messagerie).
- Détecter et prévenir la fraude.
- Vous envoyer des communications transactionnelles (confirmations, factures).
- Respecter nos obligations légales et fiscales.

## 3. Partage des renseignements
Nous **ne vendons jamais** vos renseignements. Nous les partageons uniquement avec :
- **Stripe Inc.** (traitement des paiements) — politique : https://stripe.com/privacy
- **SendGrid (Twilio Inc.)** (envoi de courriels transactionnels)
- **OpenStreetMap / Nominatim** (géocodage anonyme des adresses)
- Les autorités compétentes, lorsque la loi l'exige.

## 4. Conservation des données
Nous conservons vos renseignements aussi longtemps que votre compte est actif, plus la durée légale de prescription (6 ans au Québec pour les obligations fiscales et comptables). Vous pouvez demander la suppression à tout moment.

## 5. Vos droits (Loi 25)
Vous disposez du droit :
- **D'accès** : consulter les renseignements que nous détenons sur vous.
- **De rectification** : corriger toute information inexacte.
- **De suppression** : demander l'effacement de votre compte et de vos données.
- **De portabilité** : recevoir vos données dans un format structuré.
- **D'opposition** : refuser certains traitements (marketing notamment).
- **De retrait du consentement** à tout moment.

Pour exercer ces droits : ${contactEmail || "contact@example.com"}

## 6. Sécurité
Vos données sont stockées sur des serveurs sécurisés, chiffrées en transit (HTTPS/TLS) et au repos. L'accès est strictement limité au personnel autorisé.

## 7. Témoins (cookies)
Nous utilisons des cookies strictement nécessaires au fonctionnement (authentification, session). Aucun cookie publicitaire ou de pistage tiers n'est utilisé.

## 8. Responsable de la protection des renseignements personnels
Conformément à la Loi 25, vous pouvez joindre notre responsable à : ${contactEmail || "contact@example.com"}

## 9. Modifications
Toute modification de cette politique sera publiée sur cette page et notifiée par courriel aux utilisateurs actifs.`,
  },
  cancellation: {
    field: "cancellation_content",
    icon: RotateCcw,
    title: "Politique d'annulation et de remboursement",
    subtitle: "Conditions claires pour annuler une réservation et obtenir un remboursement.",
    testId: "legal-cancellation",
    fallback: (appName, contactEmail) => `# Politique d'annulation et de remboursement

Dernière mise à jour : ${new Date().toLocaleDateString("fr-CA")}

## Avant l'approbation par l'hôte
Tant que votre demande de réservation n'est **pas encore approuvée** par l'hôte, vous pouvez l'annuler à tout moment sans aucun frais. Aucun paiement n'est prélevé à ce stade.

## Après le paiement — Annulation par le campeur
Une fois la réservation payée :
- **Plus de 7 jours avant l'arrivée** : annulation **gratuite**, remboursement à **100 %**.
- **Entre 7 jours et 48 h avant l'arrivée** : remboursement à **70 %** (frais d'annulation de 30 %).
- **Moins de 48 h avant l'arrivée** : **annulation impossible** — aucun remboursement.

## Annulation par l'hôte
Si l'hôte annule une réservation confirmée et payée, le campeur reçoit un **remboursement intégral à 100 %** dans les 5 à 10 jours ouvrables. ${appName} se réserve le droit de sanctionner les hôtes qui annulent de façon répétée (avertissement, suspension, retrait des terrains).

## Cas de force majeure
En cas d'événement indépendant de la volonté des parties (catastrophe naturelle, fermeture gouvernementale, urgence sanitaire, etc.), un remboursement intégral est accordé sur présentation de justificatifs.

## Manquements aux règles
L'hôte peut **interrompre un séjour en cours sans remboursement** si le campeur ne respecte pas les règles communiquées (bruit, feu, dégradations, comportement dangereux, etc.). Le campeur reste seul responsable des dommages causés.

## Modalités de remboursement
Les remboursements sont effectués via Stripe sur le moyen de paiement d'origine sous **5 à 10 jours ouvrables**. Les frais de service (commission ${appName}) ne sont pas remboursés sauf en cas d'annulation par l'hôte ou de force majeure.

## Litiges
En cas de désaccord, contactez d'abord l'autre partie via la messagerie. Si aucun accord n'est trouvé, contactez ${appName} à ${contactEmail || "contact@example.com"} en joignant photos, captures d'écran et tout élément utile. Une décision sera rendue sous 7 jours ouvrables.`,
  },
};

// All HTML produced by inlineFmt is run through DOMPurify before being passed
// to React. This neutralizes any <script>, <iframe>, on*= handlers, javascript:
// URLs, etc. The allow-list below matches exactly what inlineFmt can emit.
const SAFE_HTML_CONFIG = {
  ALLOWED_TAGS: ["strong", "em", "a", "br"],
  ALLOWED_ATTR: ["href", "target", "rel", "class"],
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):)/i,
};
const sanitize = (html) => DOMPurify.sanitize(html, SAFE_HTML_CONFIG);

function renderMarkdown(text) {
  // Lightweight markdown-ish renderer: # headings, ## subheadings, **bold**, * list, blank lines as paragraph breaks
  if (!text) return null;
  const lines = text.split(/\r?\n/);
  const out = [];
  let listBuffer = [];
  const flushList = () => {
    if (listBuffer.length) {
      out.push(
        <ul key={`ul-${out.length}`} className="list-disc pl-6 space-y-1.5 text-zinc-700 my-3">
          {listBuffer.map((item, i) => (
            <li key={i} dangerouslySetInnerHTML={{ __html: sanitize(inlineFmt(item)) }} />
          ))}
        </ul>
      );
      listBuffer = [];
    }
  };
  const inlineFmt = (s) =>
    s
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
      .replace(/\[(.+?)\]\((https?:[^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-emerald-700 underline">$1</a>')
      .replace(/(https?:\/\/[^\s)]+)/g, '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-emerald-700 underline">$1</a>');

  lines.forEach((raw, idx) => {
    const line = raw.trim();
    if (!line) {
      flushList();
      return;
    }
    if (line.startsWith("# ")) {
      flushList();
      out.push(<h1 key={idx} className="font-heading text-3xl sm:text-4xl font-bold text-zinc-950 mt-8 mb-3" dangerouslySetInnerHTML={{ __html: sanitize(inlineFmt(line.slice(2))) }} />);
    } else if (line.startsWith("## ")) {
      flushList();
      out.push(<h2 key={idx} className="font-heading text-xl font-semibold text-zinc-900 mt-8 mb-2" dangerouslySetInnerHTML={{ __html: sanitize(inlineFmt(line.slice(3))) }} />);
    } else if (line.startsWith("- ") || line.startsWith("* ")) {
      listBuffer.push(line.slice(2));
    } else {
      flushList();
      out.push(<p key={idx} className="text-zinc-700 leading-relaxed my-2" dangerouslySetInnerHTML={{ __html: sanitize(inlineFmt(line)) }} />);
    }
  });
  flushList();
  return out;
}

export default function LegalPage({ which }) {
  const cfg = KEYS[which];
  const { settings } = useSiteSettings();
  const Icon = cfg.icon;
  const appName = settings?.app_name || "VroumZy";
  const contactEmail = settings?.contact_email || "";
  const content = (settings?.[cfg.field] && settings[cfg.field].trim()) || cfg.fallback(appName, contactEmail);
  // Avoid duplicate H1: if the markdown starts with "# Title", drop that first line
  // since we render our own styled hero <h1>.
  const cleaned = content.replace(/^\s*#\s+.+\r?\n/, "");

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid={cfg.testId}>
        <div className="flex items-center gap-3 mb-2">
          <div className="h-10 w-10 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center">
            <Icon className="h-5 w-5" />
          </div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-700">Légal</p>
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold text-zinc-950 leading-tight">{cfg.title}</h1>
        <p className="text-zinc-500 mt-2 text-base">{cfg.subtitle}</p>
        <div className="mt-8 prose prose-zinc max-w-none">
          {renderMarkdown(cleaned)}
        </div>
      </div>
    </div>
  );
}
